import { useEffect, useMemo } from 'react';
import {
  trackLegacyEvent,
  trackQuestionAnsweredEvent,
  trackSurveyFinishedEvent,
  trackSurveyStartedEvent,
} from '../../services/DataPipeline/DataPipeline';
import { SURVEY_SOURCE } from '../../services/DataPipeline/models';

import { EVENT, sanitizeObject } from '../../services/DataPipeline/utils';
import { OnboardingAnswers } from '../../types/onboarding/survey';

import {
  AdditionalEventData,
  ISurveyRequestData,
  ISurveys,
  SURVEY_TRIGGER,
  SURVEY_TYPES,
  SurveyPageQuestionElement,
  SurveySender,
  SurveyTrackingData,
  TimesAnswered,
} from '../../types/surveys';
import {
  getValueByNestedKeys,
  incrementTimesAnswered,
  prepareSurveyQuestionsStructure,
} from '../../utils/onboarding/survey';

export const useSurveyTracking = (
  surveyName: string,
  surveyRequestData: ISurveyRequestData,
  surveyTrigger: SURVEY_TRIGGER,
  onboardingData: ISurveys,
  onSurveyStart?: () => void,
  timesAnswered?: TimesAnswered,
  activationCycleCount = 0,
  personalizationCount = 0,
) => {
  const initTrackingData = useMemo(
    (): SurveyTrackingData => ({
      survey_name: surveyName,
      activation_cycle_count: activationCycleCount,
      survey_meta: {
        survey_version: surveyRequestData.version,
        source: SURVEY_SOURCE,
        survey_trigger: surveyTrigger,
      },
    }),
    [surveyName, surveyRequestData.version, surveyTrigger, activationCycleCount],
  );

  useEffect(() => {
    if (onboardingData.length > 0) {
      const event = sanitizeObject(initTrackingData);
      trackSurveyStartedEvent(event);
      trackLegacyEvent(
        EVENT.SURVEY_STARTED,
        JSON.stringify({
          start_time: new Date().toISOString(),
          survey_type: surveyName,
        }),
      );
      onSurveyStart?.();
    }
  }, [initTrackingData, onSurveyStart, onboardingData, surveyName]);

  const trackQuestionAnswered = (
    questionAnswered: SurveyPageQuestionElement,
    sender: SurveySender,
  ) => {
    const { titleToNameMapper: mapper } = prepareSurveyQuestionsStructure(
      onboardingData,
      sender.data,
    );

    const incrementedTimesAnswered = incrementTimesAnswered(
      SURVEY_TYPES.ONBOARDING,
      timesAnswered || ({} as TimesAnswered),
      sender.data,
    );

    const event = sanitizeObject({
      survey_name: surveyName,
      question: questionAnswered.title,
      answer: questionAnswered.value,
      survey_meta: {
        survey_version: surveyRequestData.version,
        source: SURVEY_SOURCE,
        survey_trigger: surveyTrigger,
      },
      question_answered_context: {
        question_answered_count: getValueByNestedKeys(
          incrementedTimesAnswered,
          mapper[questionAnswered.title],
        ),
      },
    });

    trackQuestionAnsweredEvent(event);
  };

  const trackSurveyFinished = (
    answers: OnboardingAnswers,
    additionalEventData: AdditionalEventData,
  ) => {
    const { surveyQuestions: questions } = prepareSurveyQuestionsStructure(onboardingData, answers);

    const event = sanitizeObject({
      ...initTrackingData,
      activation_cycle_count: activationCycleCount,
      personalization_count: personalizationCount,
      survey_questions: questions,
    });

    trackSurveyFinishedEvent(event);

    const trackingData = {
      finish_time: new Date().toISOString(),
      survey_type: surveyName,
      answers,
      ...additionalEventData,
    };

    trackLegacyEvent(EVENT.SURVEY_FINISHED, JSON.stringify(trackingData));
  };

  return {
    initTrackingData,
    trackQuestionAnswered,
    trackSurveyFinished,
  };
};
